import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { Layers, List, Plus } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useCLINs } from '../../../hooks/useCLINs';
import { useLCATs } from '../../../hooks/useLCATs';
import { exportCLINsWithLCATs, exportLaborCategories } from '../../../lib/utils/exportToExcel';
import { ExportButton } from '../ComplianceMatrix/ExportButton';
import { groupByLogicalKey } from '../v2/amendment/groupByLogicalKey';
import { HistoryToggle } from '../v2/amendment/HistoryToggle';
import { LCATDetailDrawer } from './LCATDetailDrawer';
import { LCATsTable } from './LCATsTable';
/**
 * Flatten deduped LCATs into per-CLIN rows using clinLinkages.
 * Each row gets the specific CLIN's reasoning/confidence/refs.
 */ function flattenToCLINRows(lcats) {
    const rows = [];
    for (const lcat of lcats){
        if (lcat.clinLinkages?.length) {
            for (const cl of lcat.clinLinkages){
                rows.push({
                    ...lcat,
                    clinNumber: cl.clinNumber,
                    reasoning: cl.reasoning || lcat.reasoning,
                    confidence: cl.confidence ?? lcat.confidence,
                    pwsReferences: cl.pwsReferences?.length ? cl.pwsReferences : lcat.pwsReferences,
                    attachmentReferences: cl.attachmentReferences?.length ? cl.attachmentReferences : lcat.attachmentReferences,
                    clinLinkages: undefined,
                    code: `${lcat.code}:${cl.clinNumber}`
                });
            }
        } else {
            rows.push(lcat);
        }
    }
    return rows;
}
export const LCATsTab = /*#__PURE__*/ memo(function LCATsTab({ projectId }) {
    const { data, isLoading, error } = useLCATs(projectId);
    const { data: clinData } = useCLINs(projectId);
    // Hold only the LCAT *code* in state, not the full row. The drawer
    // re-derives its `lcat` from the live useLCATs cache below so that
    // a successful mutation → cache invalidation → refetch propagates
    // into the open drawer without needing the user to close and reopen
    // (or worse, reload the whole page).
    const [selectedLCATCode, setSelectedLCATCode] = useState(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [viewMode, setViewMode] = useState('roles');
    const allLcats = data?.laborCategories || [];
    const clins = clinData?.clins || [];
    // PR-C.4: amendment-aware view defaults to current-only. The merge step
    // (PR-A) stamps every LCAT row with `logicalKey` + `isCurrentVersion`,
    // so groupByLogicalKey produces a `currentList` (one row per logical
    // role) and the totals needed by HistoryToggle. Legacy projects without
    // per-row provenance fall through to the "every row is its own group"
    // path so nothing disappears.
    const [showHistory, setShowHistory] = useState(false);
    const grouped = useMemo(()=>groupByLogicalKey(allLcats), [
        allLcats
    ]);
    const lcats = useMemo(()=>{
        if (showHistory) {
            // Render every version, latest first within each logical group.
            const out = [];
            for (const key of grouped.allKeys){
                const versions = grouped.historyByKey.get(key) ?? [];
                out.push(...versions);
            }
            return out;
        }
        return grouped.currentList;
    }, [
        grouped,
        showHistory
    ]);
    // In "by-clin" mode, flatten deduped LCATs to per-CLIN rows
    const displayLCATs = useMemo(()=>viewMode === 'by-clin' ? flattenToCLINRows(lcats) : lcats, [
        lcats,
        viewMode
    ]);
    const handleExport = useCallback((format)=>{
        if (viewMode === 'by-clin') {
            return exportCLINsWithLCATs(clins, lcats, format);
        }
        return exportLaborCategories(lcats, format);
    }, [
        clins,
        lcats,
        viewMode
    ]);
    const handleRowClick = (lcat)=>{
        setSelectedLCATCode(lcat.code);
        setIsDetailOpen(true);
    };
    const handleCloseDetail = ()=>{
        setIsDetailOpen(false);
        setSelectedLCATCode(null);
    };
    // Live drawer view: look up the current LCAT from the cache rather
    // than from a captured snapshot. After useUpdateLCAT invalidates the
    // ['lcats', projectId] query and the refetch lands, this lookup
    // re-runs and the drawer sees the new rate without a reopen/reload.
    // If the underlying row is deleted out from under us (another user,
    // another surface), the lookup returns undefined and the drawer
    // unmounts — better than showing data that no longer exists.
    const selectedLCAT = useMemo(()=>selectedLCATCode ? allLcats.find((l)=>l.code === selectedLCATCode) ?? null : null, [
        allLcats,
        selectedLCATCode
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center h-64",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "Loading..."
                    })
                ]
            })
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx("div", {
            className: "py-12 text-center",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "max-w-md mx-auto space-y-4",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-lg font-semibold text-red-600",
                        children: "Error Loading Labor Categories"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-gray-600",
                        children: error && typeof error === 'object' && 'message' in error ? String(error.message) : 'Failed to load labor categories'
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "Please check the browser console for more details or contact support."
                    })
                ]
            })
        });
    }
    if (lcats.length === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "py-12 text-center",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "max-w-md mx-auto space-y-4",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-lg font-semibold text-gray-900",
                        children: "No Labor Categories Found"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-gray-600",
                        children: "No labor categories have been extracted from your solicitation documents yet."
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-500",
                        children: "Upload solicitation documents to automatically extract labor categories."
                    })
                ]
            })
        });
    }
    // PR-B.7.3: stats panel reads from grouped.currentList instead of the
    // raw stats blob from useLCATs. Same shape as the CLINs fix — counts
    // unique logical roles, not amendment-history rows. Rate stats are
    // recomputed from the current rows so they don't double-count
    // amendment versions either.
    const currentLcats = grouped.currentList;
    const ratesPresent = currentLcats.map((l)=>l.baseHourlyRate ?? l.ceilingRate ?? 0).filter((r)=>typeof r === 'number' && r > 0);
    const avgRate = ratesPresent.length > 0 ? Math.round(ratesPresent.reduce((s, r)=>s + r, 0) / ratesPresent.length) : 0;
    const minRate = ratesPresent.length > 0 ? Math.min(...ratesPresent) : 0;
    const maxRate = ratesPresent.length > 0 ? Math.max(...ratesPresent) : 0;
    const showAmendmentBreakdown = grouped.totalRows > grouped.currentList.length;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 md:grid-cols-4 gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: showAmendmentBreakdown ? 'Current Categories' : 'Total Categories'
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: currentLcats.length.toLocaleString()
                            }),
                            showAmendmentBreakdown && /*#__PURE__*/ _jsxs("div", {
                                className: "mt-0.5 text-[11px] text-gray-400",
                                children: [
                                    "of ",
                                    grouped.totalRows.toLocaleString(),
                                    " across all amendments"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: "Average Rate"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: [
                                    "$",
                                    avgRate.toLocaleString()
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: "Min Rate"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: [
                                    "$",
                                    minRate.toLocaleString()
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: "Max Rate"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: [
                                    "$",
                                    maxRate.toLocaleString()
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "bg-white border border-gray-200 rounded-lg",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("h2", {
                                        className: "text-lg font-semibold",
                                        children: "Labor Categories"
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "text-sm text-gray-500 mt-1",
                                        children: viewMode === 'roles' ? `${grouped.currentList.length} unique roles across CLINs` : `${displayLCATs.length} CLIN-role assignments`
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-2 flex-wrap",
                                children: [
                                    /*#__PURE__*/ _jsx(HistoryToggle, {
                                        currentCount: grouped.currentList.length,
                                        totalCount: grouped.totalRows,
                                        showHistory: showHistory,
                                        onToggle: setShowHistory,
                                        noun: "labor category",
                                        nounPlural: "labor categories"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex rounded-md border border-gray-300 overflow-hidden",
                                        children: [
                                            /*#__PURE__*/ _jsxs("button", {
                                                onClick: ()=>setViewMode('roles'),
                                                className: `flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'roles' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`,
                                                children: [
                                                    /*#__PURE__*/ _jsx(Layers, {
                                                        className: "w-3.5 h-3.5"
                                                    }),
                                                    "Roles"
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("button", {
                                                onClick: ()=>setViewMode('by-clin'),
                                                className: `flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-300 ${viewMode === 'by-clin' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`,
                                                children: [
                                                    /*#__PURE__*/ _jsx(List, {
                                                        className: "w-3.5 h-3.5"
                                                    }),
                                                    "By CLIN"
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(ExportButton, {
                                        onExport: handleExport,
                                        disabled: lcats.length === 0
                                    }),
                                    /*#__PURE__*/ _jsxs(Button, {
                                        variant: "outline",
                                        size: "sm",
                                        disabled: true,
                                        children: [
                                            /*#__PURE__*/ _jsx(Plus, {
                                                className: "w-4 h-4 mr-2"
                                            }),
                                            "Add Labor Category"
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(LCATsTable, {
                        lcats: displayLCATs,
                        onRowClick: handleRowClick
                    })
                ]
            }),
            selectedLCAT && /*#__PURE__*/ _jsx(LCATDetailDrawer, {
                lcat: selectedLCAT,
                projectId: projectId,
                isOpen: isDetailOpen,
                onClose: handleCloseDetail
            })
        ]
    });
});
